package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.agent.AgentClient
import dev.lingvo.shared.agent.AgentClients
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.MouseEvent

internal val setupClients = listOf(
    AgentClient.CLAUDE_CODE, AgentClient.CODEX, AgentClient.CURSOR, AgentClient.CLAUDE_DESKTOP,
)

/** Client marks identify integrations, never customers or endorsements. */
@Composable
fun AgentClientLogo(client: AgentClient) {
    val monochrome = client == AgentClient.CODEX
    val dark = ColorMode.current == ColorMode.DARK
    val asset = when (client) {
        AgentClient.CLAUDE_CODE, AgentClient.CLAUDE_DESKTOP -> "claude.png"
        AgentClient.CODEX -> "codex.svg"
        AgentClient.CURSOR -> "cursor.svg"
        AgentClient.CI -> return
    }
    Img(src = "/brands/$asset", alt = "", attrs = {
        attr("width", "28")
        attr("height", "28")
        attr("aria-hidden", "true")
        style {
            property("object-fit", "contain")
            property("flex-shrink", "0")
            if (monochrome && dark) property("filter", "invert(1)")
        }
    })
}

/** A complete, client-specific path. No credentials are requested or stored by this public page. */
@Composable
fun AgentSetup() {
    var client by remember { mutableStateOf(AgentClient.CLAUDE_CODE) }
    var hosted by remember { mutableStateOf(false) }
    var pointerChange by remember { mutableStateOf(false) }
    val response = rememberSetupContentResponse("${client.id}:$hosted", pointerChange)
    Column(AgentSetupShellStyle.toModifier().gap(Space.XL)) {
        Div(AgentClientPickerStyle.toModifier().toAttrs {
            attr("role", "group")
            attr("aria-label", tr("marketing.agent_setup.choose_client", "Choose your client"))
        }) {
            setupClients.forEach { option ->
                Button(AgentClientChoiceStyle.toModifier().toAttrs {
                    attr("type", "button")
                    attr("aria-pressed", (option == client).toString())
                    attr("data-agent-client", option.id)
                    onClick {
                        pointerChange = (it.nativeEvent as MouseEvent).detail > 0
                        client = option
                        if (client == AgentClient.CLAUDE_DESKTOP) hosted = false
                    }
                }) {
                    AgentClientLogo(option)
                    Span { Text(option.displayName) }
                }
            }
        }
        Div(AgentSetupTransportStyle.toModifier().toAttrs {
            attr("role", "group")
            attr("aria-label", tr("marketing.agent_setup.connection_type", "Connection type"))
        }) {
            listOf(false, true).forEach { remote ->
                Button(AgentTransportChoiceStyle.toModifier().toAttrs {
                    attr("type", "button")
                    attr("aria-pressed", (hosted == remote).toString())
                    if (remote && client == AgentClient.CLAUDE_DESKTOP) attr("disabled", "")
                    onClick {
                        pointerChange = (it.nativeEvent as MouseEvent).detail > 0
                        hosted = remote
                    }
                }) {
                    Text(if (remote) t("marketing.agent_setup.hosted", "Hosted connection")
                    else t("marketing.agent_setup.local", "From your repository"))
                }
            }
        }
        P(AgentSetupNoteStyle.toModifier().toAttrs { attr("aria-live", "polite") }) {
            Text(if (hosted) t("marketing.agent_setup.hosted_description", "Works with projects stored in Lingvo. A dedicated agent token controls access and spending.")
            else t("marketing.agent_setup.local_description", "Best for working alongside your code. Your client starts Lingvo in the repository and uses your CLI sign-in."))
        }
        if (client == AgentClient.CLAUDE_DESKTOP) {
            DocsProse(t("marketing.agent_setup.desktop_availability", "This guide covers Claude Desktop on macOS. For a hosted token connection, choose Claude Code, Codex or Cursor."))
        }
        Div(AgentSetupStepsStyle.toModifier().toAttrs {
            attr("data-agent-setup-steps", client.id)
            ref { element ->
                response.element = element
                onDispose { response.element = null }
            }
        }) {
            SetupStep("1", t("marketing.agent_setup.prepare", "Prepare your project")) {
                if (hosted) {
                    DocsProse { DocsInlineLink("/app/settings/agents/new", t("marketing.agent_setup.create_connection", "Create an agent connection in Lingvo")) }
                    DocsProse(t("marketing.agent_setup.token_note", "Choose the projects and permissions it needs. Save the token in LINGVO_AGENT_TOKEN in the environment that launches your client. Keep the token out of repository files."))
                } else {
                    DocsProse { DocsInlineLink("/docs/cli#install", t("marketing.agent_setup.install_link", "Install the Lingvo CLI")) }
                    LCodeBlock("lingvo login --device\nlingvo init", t("marketing.agent_setup.sign_in", "Sign in and configure"), copyable = true)
                    DocsProse { DocsInlineLink("/docs/cli#start", t("marketing.agent_setup.new_project", "New to Lingvo? Create your hosted project and push your strings first.")) }
                }
            }
            SetupStep("2", t("marketing.agent_setup.connect", "Connect your client")) {
                val desktop = client == AgentClient.CLAUDE_DESKTOP
                if (hosted) {
                    DocsProse(t("marketing.agent_setup.merge_hosted", "Add the Lingvo server entry to the configuration file below. Keep your existing settings, then restart the client with LINGVO_AGENT_TOKEN available in its environment."))
                    LCodeBlock(AgentClients.repositoryConfig(client, remote = true), client.repositoryConfigPath.orEmpty(), copyable = true)
                    if (client == AgentClient.CODEX) {
                        DocsProse(t("marketing.agent_setup.hosted_codex_trust", "Codex reads project configuration only after you trust the project. Start a new session in this repository."))
                    }
                } else if (desktop) {
                    DocsProse(t("marketing.agent_setup.desktop_note", "In Claude Desktop, open Settings → Developer → Edit config. On macOS, add this server to mcpServers, replace the repository path, then restart Claude. Lingvo must be available on your PATH."))
                    LCodeBlock(desktopSetupConfig(), "claude_desktop_config.json", copyable = true, maxLines = 14)
                } else {
                    val command = "lingvo agent install --client ${client.id}" + if (hosted) " --transport remote" else ""
                    LCodeBlock(command, t("marketing.agent_setup.run_in_repository", "Run in your repository"), copyable = true)
                    DocsProse(t("marketing.agent_setup.existing_config", "Already have a client config? Preview with --print and merge the Lingvo entry. The installer refuses to replace existing files by default."))
                    if (client == AgentClient.CODEX) {
                        DocsProse(t("marketing.agent_setup.trusted_project", "Open this repository as a trusted project in Codex, then start a new session. Setup adds .codex/config.toml and a Lingvo skill under .agents/skills."))
                    } else {
                        DocsProse(t("marketing.agent_setup.restart", "Restart your client in this repository so it reads the new configuration."))
                    }
                }
            }
            SetupStep("3", t("marketing.agent_setup.check", "Check it, then try it")) {
                if (client == AgentClient.CODEX) {
                    LCodeBlock("codex mcp list", t("marketing.agent_setup.check_connection", "Check the connection"), copyable = true)
                    DocsProse(t("marketing.agent_setup.codex_check", "Look for lingvo in the list, then use /mcp in your session to confirm its tools are connected. Your client may ask you to approve the status read separately. A non-interactive approval refusal can happen before Lingvo receives a request, even if the client exits successfully. Check the tool result."))
                } else if (client == AgentClient.CLAUDE_CODE) {
                    LCodeBlock("claude mcp list", t("marketing.agent_setup.check_connection", "Check the connection"), copyable = true)
                    DocsProse(t("marketing.agent_setup.claude_check", "If lingvo shows Pending approval, start claude in this repository and approve only the Lingvo server from the project's .mcp.json. Run claude mcp list again and confirm lingvo shows Connected. Seeing it listed or getting a successful command result alone does not mean the connection is healthy."))
                } else {
                    DocsProse(t("marketing.agent_setup.client_check", "Open your client’s MCP settings and confirm that lingvo is connected and its tools are listed. If it is missing, check the executable path and repository directory."))
                }
                DocsProse(t("marketing.agent_setup.first_read", "Replace <project slug> with your Lingvo project. Require a successful project_status result with actual counts. This read needs qa:run and access to that project; strings:read alone is insufficient. Client approval and Lingvo permissions are separate checks."))
                LCodeBlock(
                    t("marketing.agent_setup.first_prompt", "Use Lingvo project_status for <project slug> and summarize its translation readiness. Do not change files or spend credits. If the tool is refused, report the refusal and stop."),
                    t("marketing.agent_setup.first_prompt_label", "Your first prompt"), copyable = true,
                )
            }
        }
        DocsProse { DocsInlineLink("/docs/mcp#protocol", t("marketing.agent_setup.troubleshooting", "Connection troubleshooting and protocol details")) }
    }
}

@Composable
private fun SetupStep(number: String, title: String, content: @Composable () -> Unit) {
    Div(AgentSetupStepStyle.toModifier().toAttrs()) {
        Span(AgentSetupNumberStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) { Text(number) }
        Column(Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("min-width", "0") }) {
            H3(AgentSetupStepTitleStyle.toModifier().toAttrs()) { Text(title) }
            content()
        }
    }
}

private fun desktopSetupConfig(): String = """
    {
      "mcpServers": {
        "lingvo": {
          "command": "/bin/sh",
          "args": ["-c", "cd -- \"${'$'}1\" && exec lingvo mcp", "lingvo", "/absolute/path/to/repository"]
        }
      }
    }
""".trimIndent()
